/**
 * Offline Storage Adapter — IndexedDB for Web, can be swapped for better-sqlite3 in Desktop
 *
 * Provides a table-like interface for caching Supabase data locally.
 * Each "table" is an IndexedDB object store with JSON documents keyed by `id`.
 */

const DB_NAME = 'novelforge_offline';
const DB_VERSION = 2;

const STORES = [
  'novels',
  'novel_seeds',
  'novel_worldbuilding',
  'novel_characters',
  'outline_volumes',
  'outline_arcs',
  'outline_chapters',
  'chapter_contents',
  'codex_entries',
  'codex_snapshots',
  'consistency_alerts',
  'sync_queue',
  'fact_notes',
] as const;

export type StoreName = (typeof STORES)[number];

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (event) => {
        const db = req.result;
        for (const name of STORES) {
          if (!db.objectStoreNames.contains(name)) {
            const store = db.createObjectStore(name, { keyPath: 'id' });
            // Add indexes for fact_notes to support efficient queries by category/status
            if (name === 'fact_notes') {
              store.createIndex('by_category', 'category', { unique: false });
              store.createIndex('by_status', 'status', { unique: false });
            }
          }
        }
        // Upgrade path: add indexes to fact_notes if it was already created without them
        if ((event.oldVersion ?? 0) < 2 && db.objectStoreNames.contains('fact_notes')) {
          const tx = (event.target as IDBOpenDBRequest).transaction!;
          const store = tx.objectStore('fact_notes');
          if (!store.indexNames.contains('by_category')) {
            store.createIndex('by_category', 'category', { unique: false });
          }
          if (!store.indexNames.contains('by_status')) {
            store.createIndex('by_status', 'status', { unique: false });
          }
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        dbPromise = null; // Reset so next call retries
        reject(req.error);
      };
    } catch (err) {
      // IndexedDB not available (e.g. private browsing)
      dbPromise = null;
      reject(err);
    }
  });
  return dbPromise;
}

/** Get a single record by id */
export async function getRecord<T>(store: StoreName, id: string): Promise<T | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(id);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Get all records from a store, optionally filtered by a field value */
export async function getAllRecords<T>(store: StoreName, filter?: { field: string; value: string }): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).getAll();
    req.onsuccess = () => {
      let results = req.result as T[];
      if (filter) {
        results = results.filter((r) => (r as Record<string, unknown>)[filter.field] === filter.value);
      }
      resolve(results);
    };
    req.onerror = () => reject(req.error);
  });
}

/** Put (upsert) a single record */
export async function putRecord<T extends { id: string }>(store: StoreName, record: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Put multiple records in a single transaction */
export async function putManyRecords<T extends { id: string }>(store: StoreName, records: T[]): Promise<void> {
  if (records.length === 0) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    const os = tx.objectStore(store);
    for (const r of records) {
      os.put(r);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Delete a record by id */
export async function deleteRecord(store: StoreName, id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Clear all records from a store */
export async function clearStore(store: StoreName): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Get count of records in a store */
export async function countRecords(store: StoreName): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}